/* DC Holocloth: a hanging holographic foil banner as real fabric, one per host.

   Same bones as dc-cloth.js (the hero's entrance banner): raw WebGL2, a Verlet sheet,
   no libraries. Differences, all driven by what a sponsor banner is:
   - it HANGS. The whole top edge is pinned to the rod, the bottom is free, gravity is
     real, so the sheet swings and bellies instead of billowing between four ties.
   - the material is JJ's Holocloth "Holo" preset, not black satin: base #20242D under a
     full-surface iridescence (intensity 3.78, scale 400, band 1.10, saturation 1, hue
     shift 0.37, sparkle 0.73, spec tint 0.33, roughness 0.62, metalness 1). The rainbow
     covers the whole sheet and travels with the folds; the logo is INK printed on it.
   - many instances. One shared frame loop, each sheet simulated only while on screen,
     grid sized for six on a page (32 x 20 desktop, 22 x 14 phone).

   Usage: any element with [data-dc-holo] and data-art="<logo png url>" gets a canvas
   appended and the class is-cloth. Falls back to whatever the host already shows when
   WebGL2 is missing, reduced motion is set, or anything throws.
   API: window.__dcHolo = { mount(host, opts), init(scope) }. */
(() => {
  if (window.__dcHolo) return;
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__dcForceMotion;
  const PHONE = matchMedia('(max-width: 767px)').matches;
  const NX = PHONE ? 22 : 32, NY = PHONE ? 14 : 20;
  const ITER = PHONE ? 3 : 4;
  const HEM_ITER = 2;
  const STEP = 1 / 60;
  const MAX_STEPS = PHONE ? 2 : 3;
  const GRAVITY = -520;
  const DAMP = 0.986;
  const WIND = 640;            // a light breeze through the hall, gusting
  const PLANE_K = 2.2;         // how far the belly may travel before it is pulled back
  const POINTER_R = 260;
  const POINTER_F = 1600;

  const VERT = `#version 300 es
  precision highp float;
  in vec3 aPos; in vec3 aNrm; in vec2 aUv;
  uniform mat4 uProj;
  out vec3 vN; out vec2 vUv; out vec3 vP;
  void main(){ vN=aNrm; vUv=aUv; vP=aPos; gl_Position=uProj*vec4(aPos,1.0); }`;

  const FRAG = `#version 300 es
  precision highp float;
  in vec3 vN; in vec2 vUv; in vec3 vP;
  out vec4 outColor;
  uniform sampler2D uArt;
  uniform vec3 uBase;
  uniform float uTime;
  uniform float uHoloI, uHoloS, uBand, uSat, uHue, uSparkle, uSpecTint, uRough, uMetal, uCoat, uCoatRough, uIrid;
  const vec3 L1 = normalize(vec3(-0.35, 0.60, 0.72));
  const vec3 L2 = normalize(vec3(0.55, -0.20, 0.62));
  const vec3 V = vec3(0.0, 0.0, 1.0);
  float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float ggx(vec3 n, vec3 l, float rough){
    vec3 h = normalize(l + V);
    float a = max(rough * rough, 0.002);
    float ndh = max(dot(n, h), 0.0);
    float d = (ndh * ndh) * (a * a - 1.0) + 1.0;
    float a2 = a * a;
    return (a2 * a2) / max(d * d, 1e-6);
  }
  void main(){
    vec3 n = normalize(vN);
    if (!gl_FrontFacing) n = -n;
    float ndv = max(dot(n, V), 0.0);
    float fres = pow(1.0 - ndv, 2.5);
    vec4 art = texture(uArt, vUv);

    /* Iridescence over the whole sheet: hue from the surface angle and position, so a
       fold sweeps a spectrum across itself, drifting slowly with time. PASTEL: the
       rainbow is lifted toward white so the sheet reads as pale foil, never candy. */
    float phase = (1.0 - ndv) * uHoloS * 0.02
      + (vUv.x * 0.9 + vUv.y * 0.7) * uBand * 6.2832
      + n.x * 2.6 + n.y * 2.0
      /* Blotches: real foil is not a ruled spectrum. Two slow sines make soft pools. */
      + 1.7 * sin(vUv.x * 5.3 + uTime * 0.13) * sin(vUv.y * 4.1 - uTime * 0.09)
      + 0.9 * sin(vUv.x * 2.2 - vUv.y * 3.4 + uTime * 0.07)
      + uTime * 0.18 + uHue * 6.2832;
    vec3 rainbow = 0.5 + 0.5 * cos(phase + vec3(0.0, 2.094, 4.188));
    float lum = dot(rainbow, vec3(0.333));
    rainbow = mix(vec3(lum), rainbow, uSat);
    float strength = clamp(uHoloI * 0.11, 0.0, 0.55);          // 3.78 -> 0.42
    vec3 pastel = mix(vec3(1.0), rainbow, strength);            // 0.58 .. 1.0
    vec3 paper = mix(uBase, vec3(0.96, 0.96, 0.97), 0.9);
    vec3 foil = paper * pastel;
    /* Grazing angles go more saturated and a touch brighter, like real foil. */
    foil = mix(foil, rainbow * 0.9 + 0.1, fres * uIrid * 0.5);
    /* Sparkle and grain. */
    float g = hash(floor(vUv * vec2(700.0, 420.0)));
    float g2 = hash(floor(vUv * vec2(230.0, 140.0)) + floor(uTime * 2.0) * 0.37);
    foil += (g - 0.5) * 0.07 * uSparkle;
    foil += step(0.992, g2) * 0.35 * uSparkle * (0.3 + fres);

    /* Ink sits on the foil. */
    vec3 albedo = mix(foil, art.rgb, art.a);

    float ndl1 = max(dot(n, L1), 0.0);
    float ndl2 = max(dot(n, L2), 0.0);
    float diff = 0.55 + 0.42 * ndl1 + 0.14 * ndl2;
    vec3 specTint = mix(vec3(1.0), albedo, uSpecTint * uMetal);
    float spec = ggx(n, L1, uRough) * ndl1 * 0.8 + ggx(n, L2, uRough) * ndl2 * 0.3;
    float coat = ggx(n, L1, uCoatRough) * ndl1 * uCoat;

    vec3 col = albedo * diff + specTint * spec * 0.30 + vec3(coat) * 0.25 + vec3(0.04) * fres;
    /* Sleeve: the top of the sheet sits in the shadow of whatever it hangs from. */
    col *= 1.0 - 0.22 * smoothstep(0.10, 0.0, vUv.y);
    /* Ink stays ink: no spectrum on the print. */
    col = mix(col, art.rgb * (0.6 + 0.45 * ndl1), art.a * 0.9);
    vec3 over = max(col - 0.88, vec3(0.0));
    col = min(col, vec3(0.88)) + 0.12 * over / (0.12 + over);
    outColor = vec4(col, 1.0);
  }`;

  const compile = (gl, type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader');
    return s;
  };
  const program = (gl) => {
    const p = gl.createProgram();
    gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, VERT));
    gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || 'link');
    return p;
  };

  class Cloth {
    constructor(w, h, seed) {
      this.w = w; this.h = h; this.seed = seed || 0;
      const n = NX * NY;
      this.pos = new Float32Array(n * 3); this.prev = new Float32Array(n * 3);
      this.pin = new Uint8Array(n); this.nrm = new Float32Array(n * 3); this.uv = new Float32Array(n * 2);
      for (let y = 0; y < NY; y++) for (let x = 0; x < NX; x++) {
        const i = y * NX + x, i3 = i * 3;
        const px = (x / (NX - 1) - 0.5) * w, py = -(y / (NY - 1)) * h;
        this.pos[i3] = px; this.pos[i3 + 1] = py; this.pos[i3 + 2] = 0;
        this.prev[i3] = px; this.prev[i3 + 1] = py; this.prev[i3 + 2] = 0;
        this.uv[i * 2] = x / (NX - 1); this.uv[i * 2 + 1] = y / (NY - 1);
      }
      /* Hung from the rod: the whole top row is pinned. */
      for (let x = 0; x < NX; x++) this.pin[x] = 1;
      const cons = [];
      const add = (a, b) => {
        const a3 = a * 3, b3 = b * 3;
        cons.push(a, b, Math.hypot(this.pos[a3] - this.pos[b3], this.pos[a3 + 1] - this.pos[b3 + 1]));
      };
      for (let y = 0; y < NY; y++) for (let x = 0; x < NX; x++) {
        const i = y * NX + x;
        if (x < NX - 1) add(i, i + 1);
        if (y < NY - 1) add(i, i + NX);
        if (x < NX - 1 && y < NY - 1) add(i, i + NX + 1);
        if (x > 0 && y < NY - 1) add(i, i + NX - 1);
        if (x < NX - 2) add(i, i + 2);
        if (y < NY - 2) add(i, i + NX * 2);
      }
      this.consArr = new Float32Array(cons);
      const hem = [];
      const last = (NY - 1) * NX;
      const addHem = (a, b) => { const a3 = a * 3, b3 = b * 3; hem.push(a, b, Math.hypot(this.pos[a3] - this.pos[b3], this.pos[a3 + 1] - this.pos[b3 + 1])); };
      for (let x = 0; x < NX - 1; x++) addHem(last + x, last + x + 1);
      for (let y = 0; y < NY - 1; y++) { addHem(y * NX, (y + 1) * NX); addHem(y * NX + NX - 1, (y + 1) * NX + NX - 1); }
      this.hemArr = new Float32Array(hem);
      this.idx = new Uint16Array((NX - 1) * (NY - 1) * 6);
      let k = 0;
      for (let y = 0; y < NY - 1; y++) for (let x = 0; x < NX - 1; x++) {
        const i = y * NX + x;
        this.idx[k++] = i; this.idx[k++] = i + NX; this.idx[k++] = i + 1;
        this.idx[k++] = i + 1; this.idx[k++] = i + NX; this.idx[k++] = i + NX + 1;
      }
      this.t = this.seed * 1.7;
      this.pointer = null;
    }
    step(dt) {
      this.t += dt;
      const { pos, prev, pin } = this, n = NX * NY, t = this.t, s = this.seed;
      for (let i = 0; i < n; i++) {
        if (pin[i]) continue;
        const i3 = i * 3;
        const x = pos[i3], y = pos[i3 + 1], z = pos[i3 + 2];
        const depth = clamp(-y / this.h, 0, 1);       // 0 at the hang line, 1 at the hem
        /* Gusts: a slow envelope so the breeze builds, holds and drops away, phased
           per sheet so the six never move as one. */
        const gust = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * 0.21 + s * 0.9)) * (0.6 + 0.4 * Math.sin(t * 0.067 + s * 1.7));
        /* Ripples run DOWN the sheet (depth that varies in y costs the print nothing),
           x carries one long lazy swell plus a small travelling wave near the hem. */
        const wind = (
          Math.sin(t * 1.10 + y * 0.020 + x * 0.0015 + s) * 0.55 +
          Math.sin(t * 0.57 - x * 0.0016 + s * 0.7) * 0.40 +
          Math.sin(t * 1.90 + y * 0.045 + s * 1.3) * 0.18 +
          Math.sin(t * 2.60 - x * 0.010 + y * 0.012 + s) * 0.22 * depth
        ) * gust;
        /* Sideways drift at the hem so the bottom corners wander. */
        let ax = Math.sin(t * 0.48 + y * 0.003 + s) * WIND * 0.24 * depth * gust;
        let ay = GRAVITY;
        let az = wind * WIND * (0.12 + 1.0 * depth) - z * PLANE_K;
        if (this.pointer) {
          const dx = x - this.pointer.x, dy = y - this.pointer.y, d = Math.hypot(dx, dy);
          if (d < POINTER_R) {
            const f = (1 - d / POINTER_R) * POINTER_F;
            ax += (dx / (d || 1)) * f * 0.3; ay += (dy / (d || 1)) * f * 0.3; az -= f;
          }
        }
        const nx = x + (x - prev[i3]) * DAMP + ax * dt * dt;
        const ny = y + (y - prev[i3 + 1]) * DAMP + ay * dt * dt;
        const nz = z + (z - prev[i3 + 2]) * DAMP + az * dt * dt;
        prev[i3] = x; prev[i3 + 1] = y; prev[i3 + 2] = z;
        pos[i3] = nx; pos[i3 + 1] = ny; pos[i3 + 2] = nz;
      }
      const relax = (c) => {
        for (let i = 0; i < c.length; i += 3) {
          const a3 = c[i] * 3, b3 = c[i + 1] * 3, rest = c[i + 2];
          const dx = pos[b3] - pos[a3], dy = pos[b3 + 1] - pos[a3 + 1], dz = pos[b3 + 2] - pos[a3 + 2];
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-4;
          const diff = ((d - rest) / d) * 0.5;
          const ox = dx * diff, oy = dy * diff, oz = dz * diff;
          if (!pin[c[i]]) { pos[a3] += ox; pos[a3 + 1] += oy; pos[a3 + 2] += oz; }
          if (!pin[c[i + 1]]) { pos[b3] -= ox; pos[b3 + 1] -= oy; pos[b3 + 2] -= oz; }
        }
      };
      for (let k = 0; k < ITER; k++) { relax(this.consArr); for (let j = 0; j < HEM_ITER; j++) relax(this.hemArr); }
    }
    normals() {
      const { pos, nrm, idx } = this;
      nrm.fill(0);
      for (let i = 0; i < idx.length; i += 3) {
        const a = idx[i] * 3, b = idx[i + 1] * 3, c = idx[i + 2] * 3;
        const ux = pos[b] - pos[a], uy = pos[b + 1] - pos[a + 1], uz = pos[b + 2] - pos[a + 2];
        const vx = pos[c] - pos[a], vy = pos[c + 1] - pos[a + 1], vz = pos[c + 2] - pos[a + 2];
        const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
        nrm[a] += nx; nrm[a + 1] += ny; nrm[a + 2] += nz;
        nrm[b] += nx; nrm[b + 1] += ny; nrm[b + 2] += nz;
        nrm[c] += nx; nrm[c + 1] += ny; nrm[c + 2] += nz;
      }
      for (let i = 0; i < nrm.length; i += 3) { const l = Math.hypot(nrm[i], nrm[i + 1], nrm[i + 2]) || 1; nrm[i] /= l; nrm[i + 1] /= l; nrm[i + 2] /= l; }
    }
  }
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

  /* The print: the logo PNG (white on transparent) drawn as ink into an offscreen canvas
     the size of the sheet. Loaded with crossOrigin so the texture is not tainted; the
     Shopify CDN sends the header. */
  const makeArt = (url, ink, w, h, done, rot) => {
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    if (!url) { done(c); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      /* A tall sheet prints the mark turned on its side so it reads down the cloth. */
      const aw = rot ? h : w, ah = rot ? w : h;
      const maxW = aw * 0.72, maxH = ah * 0.62;
      const k = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
      const dw = img.naturalWidth * k, dh = img.naturalHeight * k;
      ctx.clearRect(0, 0, w, h);
      ctx.save();
      ctx.translate(w / 2, h / 2);
      if (rot) ctx.rotate(-Math.PI / 2);
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
      ctx.globalCompositeOperation = 'source-in';
      ctx.fillStyle = ink;
      ctx.fillRect(0, 0, w, h);
      done(c);
    };
    img.onerror = () => done(c);
    img.src = url;
  };

  /* One loop for every sheet on the page. */
  const sheets = new Set();
  let raf = 0, last = 0;
  const loop = (now) => {
    raf = sheets.size ? requestAnimationFrame(loop) : 0;
    const dt = Math.min((now - (last || now)) / 1000, 0.25);
    last = now;
    sheets.forEach((s) => s.frame(dt));
  };
  const wake = () => { if (!raf && sheets.size) { last = 0; raf = requestAnimationFrame(loop); } };

  function mount(host, opts) {
    opts = opts || {};
    const W = opts.w || 1000, H = opts.h || 600;
    const cv = document.createElement('canvas');
    const gl = cv.getContext('webgl2', { alpha: true, antialias: true, premultipliedAlpha: false });
    if (!gl) return null;
    cv.className = 'dc-holo__canvas';
    cv.setAttribute('aria-hidden', 'true');
    host.appendChild(cv);
    const prog = program(gl);
    gl.useProgram(prog);
    const cloth = new Cloth(W, H, opts.seed || 0);
    cloth.normals();
    const buf = { pos: gl.createBuffer(), nrm: gl.createBuffer(), uv: gl.createBuffer(), idx: gl.createBuffer() };
    const loc = { pos: gl.getAttribLocation(prog, 'aPos'), nrm: gl.getAttribLocation(prog, 'aNrm'), uv: gl.getAttribLocation(prog, 'aUv') };
    const u = (n) => gl.getUniformLocation(prog, n);
    gl.bindBuffer(gl.ARRAY_BUFFER, buf.uv); gl.bufferData(gl.ARRAY_BUFFER, cloth.uv, gl.STATIC_DRAW);
    gl.enableVertexAttribArray(loc.uv); gl.vertexAttribPointer(loc.uv, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, buf.idx); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, cloth.idx, gl.STATIC_DRAW);

    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const texW = W >= H ? 1024 : Math.round(1024 * W / H), texH = W >= H ? Math.round(1024 * H / W) : 1024;
    makeArt(opts.art, opts.ink || '#14161a', texW, texH, (c) => {
      try { gl.bindTexture(gl.TEXTURE_2D, tex); gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c); } catch (e) {}
    }, !!opts.rot);

    const pad = 40;
    const proj = new Float32Array(16);
    proj[0] = 2 / (W + pad * 2); proj[5] = 2 / (H + pad * 2); proj[10] = -1 / 1200; proj[15] = 1;
    proj[13] = 1 - (2 * pad) / (H + pad * 2);
    gl.uniformMatrix4fv(u('uProj'), false, proj);
    const p = Object.assign({ base: [0x20 / 255, 0x24 / 255, 0x2d / 255], holoI: 3.78, holoS: 400, band: 1.10, sat: 1.0, hue: 0.37, sparkle: 0.73, specTint: 0.33, rough: 0.62, metal: 1.0, coat: 0.06, coatRough: 0.70, irid: 0.81 }, opts.preset || {});
    gl.uniform3f(u('uBase'), p.base[0], p.base[1], p.base[2]);
    gl.uniform1f(u('uHoloI'), p.holoI); gl.uniform1f(u('uHoloS'), p.holoS); gl.uniform1f(u('uBand'), p.band);
    gl.uniform1f(u('uSat'), p.sat); gl.uniform1f(u('uHue'), p.hue); gl.uniform1f(u('uSparkle'), p.sparkle);
    gl.uniform1f(u('uSpecTint'), p.specTint); gl.uniform1f(u('uRough'), p.rough); gl.uniform1f(u('uMetal'), p.metal);
    gl.uniform1f(u('uCoat'), p.coat); gl.uniform1f(u('uCoatRough'), p.coatRough); gl.uniform1f(u('uIrid'), p.irid);
    gl.uniform1i(u('uArt'), 0);
    const uTime = u('uTime');
    gl.enable(gl.DEPTH_TEST); gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    /* Layout size, not the bounding box: the section's entrance holds the sheet at
       scaleY(0) while this mounts, and a bounding box read then is 2 px tall. */
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(2, Math.round(host.offsetWidth * dpr)), h = Math.max(2, Math.round(host.offsetHeight * dpr));
      if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
      gl.viewport(0, 0, cv.width, cv.height);
    };
    resize();
    const ro = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    if (ro) ro.observe(host);

    const onMove = (e) => {
      const r = host.getBoundingClientRect();
      if (!r.width) return;
      cloth.pointer = { x: ((e.clientX - r.left) / r.width - 0.5) * W, y: -((e.clientY - r.top) / r.height) * H };
      wake();
    };
    const onLeave = () => { cloth.pointer = null; };
    host.addEventListener('pointermove', onMove, { passive: true });
    host.addEventListener('pointerleave', onLeave, { passive: true });

    let visible = false, acc = 0;
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((es) => { visible = es.some((x) => x.isIntersecting); if (visible) wake(); }, { threshold: 0.01 }) : null;
    if (io) io.observe(host); else visible = true;

    const sheet = {
      frame(dt) {
        if (!visible) return;
        acc += dt;
        let steps = 0;
        while (acc >= STEP && steps < MAX_STEPS) { cloth.step(STEP); acc -= STEP; steps++; }
        if (acc > STEP * MAX_STEPS) acc = 0;
        cloth.normals();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf.pos); gl.bufferData(gl.ARRAY_BUFFER, cloth.pos, gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(loc.pos); gl.vertexAttribPointer(loc.pos, 3, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, buf.nrm); gl.bufferData(gl.ARRAY_BUFFER, cloth.nrm, gl.DYNAMIC_DRAW);
        gl.enableVertexAttribArray(loc.nrm); gl.vertexAttribPointer(loc.nrm, 3, gl.FLOAT, false, 0, 0);
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.uniform1f(uTime, cloth.t);
        gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.drawElements(gl.TRIANGLES, cloth.idx.length, gl.UNSIGNED_SHORT, 0);
      }
    };
    sheets.add(sheet); wake();
    host.classList.add('is-cloth');
    return {
      destroy() {
        sheets.delete(sheet);
        try { ro && ro.disconnect(); } catch (e) {}
        try { io && io.disconnect(); } catch (e) {}
        host.removeEventListener('pointermove', onMove);
        host.removeEventListener('pointerleave', onLeave);
        host.classList.remove('is-cloth');
        try { gl.getExtension('WEBGL_lose_context') && gl.getExtension('WEBGL_lose_context').loseContext(); } catch (e) {}
        cv.remove();
      }
    };
  }

  const init = (scope) => {
    if (REDUCED) return;
    (scope || document).querySelectorAll('[data-dc-holo]').forEach((host, i) => {
      if (host.__dcHolo) return;
      /* "lazy" hosts are mounted by their own section when it nears the viewport. */
      if (host.dataset.dcHolo === 'lazy' && !(scope && scope !== document)) return;
      try {
        const inst = mount(host, {
          art: host.dataset.art || '',
          ink: host.dataset.ink || '#14161a',
          w: parseFloat(host.dataset.w) || 1000,
          h: parseFloat(host.dataset.h) || 625,
          rot: host.dataset.rot === 'true',
          seed: parseFloat(host.dataset.seed) || i
        });
        if (inst) host.__dcHolo = inst;
      } catch (e) {
        if (host.__dcHolo) { try { host.__dcHolo.destroy(); } catch (e2) {} host.__dcHolo = null; }
      }
    });
  };
  const destroy = (scope) => (scope || document).querySelectorAll('[data-dc-holo]').forEach((host) => { if (host.__dcHolo) { try { host.__dcHolo.destroy(); } catch (e) {} host.__dcHolo = null; } });

  window.__dcHolo = { mount, init, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => init(e.target));
    document.addEventListener('shopify:section:unload', (e) => destroy(e.target));
  }
})();
